import type { AccessArea, AccessAssignment, AccessLevel } from "./types";

export type PersonAccessMode = "none" | "full" | "areas";

export interface AreaGrantForm {
  enabled: boolean;
  level: AccessLevel;
  approves: boolean;
  notify: boolean;
}

// Sign-off is only ever given per area, so someone with the whole workbook can
// still approve chosen areas.
export interface PersonAccessForm {
  mode: PersonAccessMode;
  fullLevel: AccessLevel;
  fullNotify: boolean;
  areas: Record<string, AreaGrantForm>;
}

const emptyArea: AreaGrantForm = {
  enabled: false,
  level: "view",
  approves: false,
  notify: false,
};

export const toPersonAccessForm = (
  rows: AccessAssignment[],
  areas: AccessArea[],
): PersonAccessForm => {
  const full = rows.find((row) => row.full_workbook_access);
  const byKey = new Map(
    rows
      .filter((row) => !row.full_workbook_access)
      .map((row) => [row.access_key, row]),
  );
  return {
    mode: full ? "full" : byKey.size > 0 ? "areas" : "none",
    fullLevel: full?.access_level ?? "edit",
    fullNotify: full?.notify_pack_questions ?? false,
    areas: Object.fromEntries(
      areas.map((area) => {
        const row = byKey.get(area.key);
        return [
          area.key,
          row
            ? {
                enabled: true,
                level: row.access_level,
                approves: row.approval_access,
                notify: row.notify_pack_questions,
              }
            : emptyArea,
        ];
      }),
    ),
  };
};

// Rows the person held on areas the page does not list (a retired area) are
// carried over untouched, so editing someone never drops a grant out of sight.
export const fromPersonAccessForm = (
  userId: string,
  form: PersonAccessForm,
  previous: AccessAssignment[],
  areas: AccessArea[],
): AccessAssignment[] => {
  const listed = new Set(areas.map((area) => area.key));
  const hidden = previous.filter(
    (row) =>
      !row.full_workbook_access &&
      row.access_key &&
      !listed.has(row.access_key),
  );
  if (form.mode === "none") return hidden;
  if (form.mode === "full") {
    // A sheet row stays while it adds something to the whole workbook: sign-off,
    // edit beside whole-workbook view, or emails the whole-workbook row does not
    // send. Applying without changes never drops one.
    const sheetRows = areas.flatMap((area) => {
      const before = previous.find(
        (row) => !row.full_workbook_access && row.access_key === area.key,
      );
      const approves = form.areas[area.key]?.approves ?? false;
      const addsEdit =
        before?.access_level === "edit" && form.fullLevel === "view";
      const addsEmail = !!before?.notify_pack_questions && !form.fullNotify;
      if (!approves && !addsEdit && !addsEmail) return [];
      return [
        {
          user_id: userId,
          full_workbook_access: false,
          access_key: area.key,
          access_level: before?.access_level ?? form.fullLevel,
          approval_access: approves,
          notify_pack_questions: before?.notify_pack_questions ?? false,
        },
      ];
    });
    return [
      {
        user_id: userId,
        full_workbook_access: true,
        access_key: null,
        access_level: form.fullLevel,
        approval_access: false,
        notify_pack_questions: form.fullNotify,
      },
      ...sheetRows,
      ...hidden,
    ];
  }
  const granted = areas
    .filter((area) => form.areas[area.key]?.enabled)
    .map((area) => {
      const grant = form.areas[area.key];
      return {
        user_id: userId,
        full_workbook_access: false,
        access_key: area.key,
        access_level: grant.level,
        approval_access: grant.approves,
        notify_pack_questions: grant.notify,
      };
    });
  return [...granted, ...hidden];
};
